import { useEffect, useState } from 'react';
import { StockEntry } from './StockEntry';
import { ScenarioSelector } from './ScenarioSelector';
import { ScenarioType, StockData, StockScenarioData } from './types';
import StockService from '../../Api_Module/Api_Services/StockService';
import { StockSearch } from '../StockSearch';
interface WatchlistTableProps {
  readonly selectedScenario: ScenarioType;
  readonly onScenarioChange: (scenario: ScenarioType) => void;
}

export function WatchlistTable({
  selectedScenario,
  onScenarioChange
}: WatchlistTableProps) {
  const [stockData, setStockData] = useState<StockData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const getStockResearchDashboard = async () => {
    try {
      setIsLoading(true);
      const response = await StockService.getDashboard();
      if (response && Array.isArray(response)) {
        // Group the API response by stock symbol
        const groupedData = response.reduce<Record<string, StockData>>((acc, item: StockScenarioData & Record<string, any>) => {
          const sym = item.stockSymbol;
          // Normalize possible API key variants
          let logoUrl: string | undefined = item.logoUrl || item.logo || item.logo_url || item.logoURL;
          if (!logoUrl && typeof sym === 'string' && sym.trim()) {
            // Construct a sensible default logo URL (commonly used CDN)
            logoUrl = `https://images.financialmodelingprep.com/symbol/${encodeURIComponent(sym.trim().toUpperCase())}.png`;
          }
          const currentPrice: number | undefined =
            typeof item.currentPrice === 'number' ? item.currentPrice :
            typeof item.current_price === 'number' ? item.current_price :
            typeof item.price === 'number' ? item.price :
            typeof item.analystsBasePE === 'number' ? item.analystsBasePE :
            undefined;
          if (!acc[sym]) {
            acc[sym] = {
              symbol: sym,
              name: sym,
              scenarios: {},
              currentPrice,
              logo: logoUrl
            } as StockData;
          } else {
            // Update price/logo if present on subsequent items
            if (typeof currentPrice === 'number') acc[sym].currentPrice = currentPrice;
            if (logoUrl) acc[sym].logo = logoUrl;
          }
          // Type assertion needed here since we know the exact shape
          (acc[sym].scenarios as Record<ScenarioType, StockScenarioData>)[item.scenarioKey] = item;
          return acc;
        }, {});

        setStockData(Object.values(groupedData));
      }
    } catch (error) {
      console.error('Error fetching market data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getStockResearchDashboard();
  }, []);

  const filteredStocks = stockData
    .slice()
    .sort((a, b) => a.symbol.localeCompare(b.symbol));

  return <div className="bg-theme-secondary rounded-lg shadow-lg border border-theme-tertiary overflow-hidden">
      <div className="p-4 border-b border-theme-tertiary flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="relative w-full max-w-md">
          <StockSearch placeholder="Search stocks..." className="w-full" />
        </div>
        <div className="w-full md:w-auto">
          <ScenarioSelector selectedScenario={selectedScenario} onScenarioChange={onScenarioChange} />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-theme-tertiary">
            <tr>
              <th className="py-3 px-6 text-left text-xs font-medium text-theme-secondary uppercase tracking-wider">Company</th>
              <th className="py-3 px-6 text-right text-xs font-medium text-theme-secondary uppercase tracking-wider">Price</th>
              <th className="py-3 px-6 text-right text-xs font-medium text-theme-secondary uppercase tracking-wider">Analyst 3YR Target</th>
              <th className="py-3 px-6 text-right text-xs font-medium text-theme-secondary uppercase tracking-wider">Your 1YR Target</th>
              <th className="py-3 px-6 text-right text-xs font-medium text-theme-secondary uppercase tracking-wider">Your 3YR Target</th>
              <th className="py-3 px-6 text-right text-xs font-medium text-theme-secondary uppercase tracking-wider">Your 5YR Target</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-theme-tertiary">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-theme-secondary">
                  Loading data...
                </td>
              </tr>
            ) : filteredStocks.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-theme-secondary">
                  No stocks found matching your search criteria
                </td>
              </tr>
            ) : (
              filteredStocks.map((stock) => (
                <StockEntry 
                  key={stock.symbol} 
                  stock={stock} 
                  selectedScenario={selectedScenario} 
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-theme-tertiary flex justify-start items-center text-xs text-theme-secondary">
        <div>Showing {filteredStocks.length} of {stockData.length} stocks</div>
      </div>
    </div>;
}